<template>
  <div class="appintegration-component">
    <IslandBadge type="appintegration"></IslandBadge>
    <div class="component-contents">
      <button v-if="isLoading" :class="{ 'is-loading': isLoading }" class="button is-text"></button>
      <div v-if="!isLoading && !card">{{ getSelectedAppDescription() }}</div>
      <CardRenderer
        v-if="!isLoading && card"
        :colour="actionColour"
        :cards="[card]"
        :location="'embedded'"
        :readonly="true"
      />
      <div v-if="shouldShowEditButton()" class="appintegration-component__edit">
        <n-button @click="openAppEditModal()" variant="ghost"><EditIcon />Edit</n-button>
      </div>
    </div>

    <CapableAppModal
      :modalHeading="'Bot Apps'"
      :capableApps="capableApps"
      :configRequest="configRequest"
      v-if="showModal"
      @close="close"
      @completed="renderAppPreview"
    />
    <ContentHighlightsEditModal
      v-if="showAppEditModal"
      :app-config="value.appAction!"
      @cancel="showAppEditModal = false"
      @save="(app) => handleAppSave(app)"
    />
  </div>
</template>

<script lang="ts" setup>
import { StateViewModel } from '@/features/bots/models/designer';
import { appsApi } from '@/features/apps/shared/services/apps';
import { InstalledApp } from '@/features/apps/shared/models';
import CapableAppModal from '@/shared/components/capableAppPicker/CapableAppModal.vue';
import IslandBadge from './IslandBadge.vue';
import { botsApi } from '../../services/bots';
import CardRenderer from '@/shared/components/cardRenderer/CardRenderer.vue';
import { Card } from '@/shared/components/cardRenderer';
import { AppCard } from '@/features/apps/shared/models/appCard';
import { BotAppInitViewModel } from '@/features/bots/models/request/botAppInitViewModel';
import { DELETE_STATE } from '@/store/bot-designer/states/actionTypes';
import ContentHighlightsEditModal from '@/shared/components/contentHighlights/ContentHighlightsEditModal.vue';
import EditIcon from 'vue-material-design-icons/SquareEditOutline.vue';
import { ConfiguredAppIntegration } from '@/features/apps/shared/models/configuredAppIntegration';
import { computed, onBeforeMount, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  value: {
    type: Object as PropType<StateViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'state-updated', value: StateViewModel): void;
}>();

const state = ref<StateViewModel>({
  id: '',
  component: 'AppIntegration',
});

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);

const removeState = (stateToRemove: StateViewModel) => store.dispatch(DELETE_STATE, stateToRemove);

const actionColour = ref('#062B80');
const card = ref<Card>();
const capableApps = ref<InstalledApp[]>([]);
const showModal = ref(false);
const isLoading = ref(false);
const showAppEditModal = ref(false);

watch(
  () => props.value,
  (value) => (state.value = value),
  { immediate: true },
);

onBeforeMount(async () => {
  isLoading.value = true;
  await initialiseApps();
});

function close() {
  showModal.value = false;

  if (!state.value.appAction) {
    removeState(state.value);
  }
}

async function renderAppPreview(installedAppId: string, config: any) {
  isLoading.value = true;

  state.value.appAction = {
    installedAppId,
    config,
  };

  try {
    const request = { config } as BotAppInitViewModel;
    const appInitData = await botsApi.botAppInit(currentWorkspace.value.id, installedAppId, request);
    if (isPanelComponentsValid(appInitData.card)) {
      state.value.appAction.preview = JSON.stringify(appInitData.card.components);
      card.value = { id: appInitData.id, content: appInitData.card };
    }
  } catch {
    // just carry on
  } finally {
    isLoading.value = false;
  }

  emit('state-updated', state.value);
}

function getSelectedAppDescription() {
  let app: InstalledApp | undefined;
  if (state.value.appAction && state.value.appAction) {
    const installedAppId = state.value.appAction.installedAppId;
    app = capableApps.value.find((a) => a.installedAppId === installedAppId);
  }

  if (!app) {
    return '';
  }

  return app.name;
}

const configRequest = (workspaceId: string, installedAppId: string, values: any, storedData: any, componentId: any) => {
  return botsApi.botAppConfig(workspaceId, installedAppId, values, storedData, componentId);
};

function handleAppSave(app: ConfiguredAppIntegration) {
  showAppEditModal.value = false;
  state.value.appAction = app;
  renderAppPreview(state.value.appAction.installedAppId, state.value.appAction.config);
  emit('state-updated', state.value);
}

function openAppEditModal() {
  showAppEditModal.value = true;
}

function shouldShowEditButton() {
  const contentHighlightsApp = capableApps.value.find((app) => app.appId === 'contenthighlights');

  if (!contentHighlightsApp) {
    return false;
  }

  return props.value.appAction?.installedAppId === contentHighlightsApp.installedAppId;
}

async function initialiseApps() {
  try {
    const apps = await appsApi.getCapableApps(currentWorkspace.value.id, ['botActions']);
    capableApps.value = apps.map((app) => app as InstalledApp);
    if (!state.value.appAction) {
      showModal.value = true;
    } else {
      if (state.value.appAction.preview) {
        card.value = {
          id: state.value.appAction.installedAppId,
          content: {
            components: JSON.parse(state.value.appAction.preview),
          },
        };
      }
    }
  } finally {
    isLoading.value = false;
  }
}

function isPanelComponentsValid(panel: AppCard) {
  return panel && panel.components && panel.components.length > 0;
}
</script>

<style scoped lang="scss">
.appintegration-component {
  display: flex;
  flex: 1;
  border-radius: 5px;
  position: relative;
  &__edit {
    display: flex;
    justify-content: flex-end;
    :deep(.material-design-icon) {
      margin-right: 0.5rem;
    }
  }
}

.component-contents {
  padding-right: 2em;
  flex-direction: column;
}
</style>
